// Outglow reputation tiers — direct port of mobile/src/utils/reputation.js
// so the web matches the mobile tier system exactly. If you change the
// tier table on one platform, change it on both — the server's
// utils/reputationRewards.js is the third source of truth.

import { applyPresetToTier } from './tierNames';

const BASE_TIERS = [
  {
    key: 'rookie_frame', min: 0, title: 'Rookie Frame', badgeLabel: 'Rookie',
    tone: 'muted', icon: '✦', voteWeight: 1.0,
    color: {
      fg: '#B7AEC7', bg: 'rgba(120,114,140,0.14)', border: '#3A3346',
      chip: '#6B6680', glow: 'rgba(140,134,165,0.45)'
    },
    frame: { ring: 'solid', colors: ['#5C546C'], glow: 'rgba(140,134,165,0.30)', animated: false }
  },
  {
    key: 'spark_shooter', min: 25, title: 'Spark Shooter', badgeLabel: 'Spark',
    tone: 'primary', icon: '⚡', voteWeight: 1.0,
    color: {
      fg: '#5EE89F', bg: 'rgba(34,214,136,0.10)', border: 'rgba(34,214,136,0.40)',
      chip: '#1FA869', glow: 'rgba(34,214,136,0.55)'
    },
    frame: { ring: 'solid', colors: ['#1FA869'], glow: 'rgba(34,214,136,0.45)', animated: false }
  },
  {
    key: 'arena_contender', min: 75, title: 'Arena Contender', badgeLabel: 'Contender',
    tone: 'accent', icon: '🎗', voteWeight: 1.0,
    color: {
      fg: '#4DDCF2', bg: 'rgba(16,194,232,0.10)', border: 'rgba(16,194,232,0.45)',
      chip: '#0E9CBA', glow: 'rgba(16,194,232,0.55)'
    },
    frame: { ring: 'solid', colors: ['#0E9CBA'], glow: 'rgba(16,194,232,0.50)', animated: false }
  },
  {
    key: 'framebreaker', min: 150, title: 'Framebreaker', badgeLabel: 'Framebreaker',
    tone: 'warning', icon: '◎', voteWeight: 1.10,
    color: {
      fg: '#7AA2FF', bg: 'rgba(61,126,255,0.12)', border: 'rgba(61,126,255,0.50)',
      chip: '#3568DF', glow: 'rgba(61,126,255,0.60)'
    },
    frame: { ring: 'gradient', colors: ['#3568DF', '#7AA2FF'], glow: 'rgba(61,126,255,0.55)', animated: false }
  },
  {
    key: 'vanguard_creator', min: 300, title: 'Vanguard Creator', badgeLabel: 'Vanguard',
    tone: 'primary', icon: '🛡', voteWeight: 1.20,
    color: {
      fg: '#C09CFF', bg: 'rgba(155,89,255,0.14)', border: 'rgba(155,89,255,0.55)',
      chip: '#8A47F0', glow: 'rgba(155,89,255,0.65)'
    },
    frame: { ring: 'gradient', colors: ['#8A47F0', '#C09CFF'], glow: 'rgba(155,89,255,0.60)', animated: false }
  },
  {
    key: 'icon_maker', min: 600, title: 'Icon Maker', badgeLabel: 'Icon',
    tone: 'gold', icon: '★', voteWeight: 1.30,
    color: {
      fg: '#FF9BD5', bg: 'rgba(233,64,176,0.16)', border: 'rgba(233,64,176,0.60)',
      chip: '#D8369C', glow: 'rgba(233,64,176,0.70)'
    },
    frame: { ring: 'gradient', colors: ['#FF3E7F', '#FF9BD5', '#FFB347'], glow: 'rgba(255,108,168,0.65)', animated: false }
  },
  {
    key: 'clash_legend', min: 1000, title: 'Clash Legend', badgeLabel: 'Legend',
    tone: 'gold', icon: '🏆', voteWeight: 1.40,
    color: {
      fg: '#FF9670', bg: 'rgba(255,85,68,0.18)', border: 'rgba(255,85,68,0.65)',
      chip: '#E84A30', glow: 'rgba(255,107,74,0.75)'
    },
    frame: { ring: 'gradient', colors: ['#E84A30', '#FF9670', '#FFD166'], glow: 'rgba(255,107,74,0.75)', animated: true }
  },
  {
    key: 'mythic_frame', min: 2000, title: 'Mythic Frame', badgeLabel: 'Mythic',
    tone: 'gold', icon: '◆', voteWeight: 1.50,
    color: {
      fg: '#FFE9B8', bg: 'rgba(255,179,71,0.18)', border: 'rgba(255,179,71,0.85)',
      chip: '#FFB347', glow: 'rgba(255,179,71,0.85)', prismatic: true
    },
    frame: { ring: 'prismatic', colors: ['#FF3E7F', '#FFB347', '#5BE9FF', '#C09CFF', '#FF3E7F'], glow: 'rgba(255,179,71,0.85)', animated: true }
  }
];

function decorate(i) {
  return applyPresetToTier(BASE_TIERS[i], i);
}

export function getReputationTiers() {
  return BASE_TIERS.map((_, i) => decorate(i));
}

export function tierForReputation(value = 0) {
  const rep = Number(value || 0);
  for (let i = BASE_TIERS.length - 1; i >= 0; i--) {
    if (rep >= BASE_TIERS[i].min) return decorate(i);
  }
  return decorate(0);
}

export function nextTierForReputation(value = 0) {
  const rep = Number(value || 0);
  const idx = BASE_TIERS.findIndex((tier) => rep < tier.min);
  return idx === -1 ? null : decorate(idx);
}

export function voteWeightForReputation(value = 0) {
  return tierForReputation(value).voteWeight;
}

export function frameForUser(user) {
  if (user?.reputationRewards?.frame?.colors) return user.reputationRewards.frame;
  return tierForReputation(user?.reputation).frame;
}

export function rewardProgress(value = 0) {
  const rep = Number(value || 0);
  const current = tierForReputation(rep);
  const next = nextTierForReputation(rep);
  if (!next) return { ratio: 1, remaining: 0, next: null, current };
  const span = Math.max(1, next.min - current.min);
  return {
    ratio: Math.max(0, Math.min(1, (rep - current.min) / span)),
    remaining: Math.max(0, next.min - rep),
    current,
    next
  };
}
